// src/pages/sales/PageVisitReports.jsx
import { useState, useEffect, useCallback } from "react";
import * as api from "../../apiService";
import { fmtDate, fmtDateTime, currentMonthKey, monthOptions } from "../../utils/dateUtils";
import { VisitLogModal } from "./VisitLogModal";
import { useToast } from "../../components/Toast";

const CHANNEL_LABEL = { PHONE:"Phone", VISIT:"Visit", EMAIL:"Email", WHATSAPP:"WhatsApp" };
const CHANNEL_COLOR = {
  PHONE:    { bg:"#eff6ff", color:"#1d4ed8" },
  VISIT:    { bg:"#f0fdf4", color:"#15803d" },
  EMAIL:    { bg:"#fdf4ff", color:"#7e22ce" },
  WHATSAPP: { bg:"#f0fdfa", color:"#0d9488" },
};

function ChannelBadge({ channel }) {
  const s = CHANNEL_COLOR[channel] ?? { bg:"#f3f4f6", color:"#374151" };
  return (
    <span style={{ background:s.bg, color:s.color, borderRadius:4,
      padding:"2px 8px", fontSize:11, fontWeight:600 }}>
      {CHANNEL_LABEL[channel] ?? channel}
    </span>
  );
}

function fmt2(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("en-AE", { minimumFractionDigits:2, maximumFractionDigits:2 });
}

export function PageVisitReports({ myPerms, userEmail }) {
  const showToast = useToast();
  const canEdit    = myPerms?.includes("VISIT_LOG_EDIT");
  const canViewAll = myPerms?.includes("VISIT_LOG_VIEW_ALL");

  const [month,      setMonth]      = useState(currentMonthKey());
  const [spSearch,   setSpSearch]   = useState("");
  const [logs,       setLogs]       = useState([]);
  const [accounts,   setAccounts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [modalOpen,  setModalOpen]  = useState(false);

  useEffect(() => {
    api.fetchCrmAccounts()
      .then(rows => setAccounts(rows))
      .catch(() => setAccounts([]));
  }, []); // eslint-disable-line

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    // Load all logs for the month — salesperson filtering is done locally
    api.fetchVisitLogs(month)
      .then(setLogs)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [month]); // eslint-disable-line

  useEffect(() => { load(); }, [load]); // eslint-disable-line

  // Filter by salesperson name or email as the user types
  const q = spSearch.trim().toLowerCase();
  const filteredLogs = canViewAll && q
    ? logs.filter(l =>
        (l.salespersonName  ?? "").toLowerCase().includes(q) ||
        (l.salespersonEmail ?? "").toLowerCase().includes(q))
    : logs;

  // Compute summary from filtered logs — no separate API call needed
  const summary = !loading ? {
    phone:              filteredLogs.filter(l => l.channel === "PHONE").length,
    visit:              filteredLogs.filter(l => l.channel === "VISIT").length,
    email:              filteredLogs.filter(l => l.channel === "EMAIL").length,
    whatsapp:           filteredLogs.filter(l => l.channel === "WHATSAPP").length,
    total:              filteredLogs.length,
    totalSalesAed:      filteredLogs.reduce((s, l) => s + (Number(l.salesAed)      || 0), 0),
    totalCollectionAed: filteredLogs.reduce((s, l) => s + (Number(l.collectionAed) || 0), 0),
  } : null;

  async function handleSave(body) {
    await api.createVisitLog(body);
    showToast("Visit log saved");
    setModalOpen(false);
    load();
  }

  const months = monthOptions();

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Daily Visit Reports</div>
          <div className="page-subtitle">
            Customer contacts · {filteredLogs.length}{q ? ` / ${logs.length}` : ""} log{filteredLogs.length !== 1 ? "s" : ""} this period
          </div>
        </div>
        {canEdit && (
          <div className="page-header-right">
            <button className="btn-primary" onClick={() => setModalOpen(true)}>+ Log Contact</button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="action-bar" style={{ gap:10 }}>
        <select className="form-input" style={{ width:160 }}
          value={month} onChange={e => setMonth(e.target.value)}>
          {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        {canViewAll && (
          <input className="form-input" style={{ width:260 }}
            placeholder="Search by name or email…"
            value={spSearch}
            onChange={e => setSpSearch(e.target.value)} />
        )}
      </div>

      {/* Monthly summary panel */}
      {summary && (
        <div className="card" style={{ marginBottom:16, padding:"14px 20px" }}>
          <div style={{ display:"flex", gap:20, flexWrap:"wrap", alignItems:"center" }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#6b7280", minWidth:80 }}>
              {months.find(m => m.value === month)?.label ?? month}
            </div>
            {["PHONE","VISIT","EMAIL","WHATSAPP"].map(ch => {
              const count = summary[ch.toLowerCase()] ?? 0;
              const s = CHANNEL_COLOR[ch];
              return (
                <div key={ch} style={{ display:"flex", alignItems:"center", gap:5 }}>
                  <span style={{ background:s.bg, color:s.color, borderRadius:4,
                    padding:"2px 8px", fontSize:11, fontWeight:600 }}>
                    {CHANNEL_LABEL[ch]}
                  </span>
                  <span style={{ fontSize:14, fontWeight:700 }}>{count}</span>
                </div>
              );
            })}
            <div style={{ marginLeft:"auto", display:"flex", gap:28 }}>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>Total Contacts</div>
                <div style={{ fontSize:18, fontWeight:700 }}>{summary.total ?? 0}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>Sales (AED)</div>
                <div style={{ fontSize:18, fontWeight:700, color:"#15803d" }}>
                  {fmt2(summary.totalSalesAed)}
                </div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>Collections (AED)</div>
                <div style={{ fontSize:18, fontWeight:700, color:"#1d4ed8" }}>
                  {fmt2(summary.totalCollectionAed)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && <div style={{ color:"#aaa", padding:"24px 0" }}>Loading…</div>}
      {error   && <div className="alert-warn">{error}</div>}

      {!loading && !error && (
        <div className="card">
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr>
                <th>Date</th>
                {canViewAll && <th>Salesperson</th>}
                <th>Customer</th>
                <th>Channel</th>
                <th>Summary</th>
                <th style={{ textAlign:"right" }}>Sales (AED)</th>
                <th style={{ textAlign:"right" }}>Collection (AED)</th>
                <th>Recorded On</th>
              </tr></thead>
              <tbody>
                {filteredLogs.length === 0 && (
                  <tr>
                    <td colSpan={canViewAll ? 8 : 7}
                      style={{ textAlign:"center", color:"#aaa", padding:"32px 0" }}>
                      {q ? `No logs matching "${spSearch}".` : "No contacts logged for this period."}
                    </td>
                  </tr>
                )}
                {filteredLogs.map(log => (
                  <tr key={log.id}>
                    <td style={{ whiteSpace:"nowrap" }}>{fmtDate(log.contactDate)}</td>
                    {canViewAll && (
                      <td style={{ fontSize:12 }}>
                        <div className="fw-600">{log.salespersonName}</div>
                        <div style={{ color:"#9ca3af", fontSize:11 }}>{log.salespersonEmail}</div>
                      </td>
                    )}
                    <td className="fw-600">
                      {log.accountId
                        ? (accounts.find(a => a.id === log.accountId)?.name ?? `Account #${log.accountId}`)
                        : (
                          <span>
                            {log.prospectName}
                            <span className="badge badge-gray" style={{ marginLeft:6, fontSize:9 }}>Prospect</span>
                          </span>
                        )
                      }
                    </td>
                    <td><ChannelBadge channel={log.channel} /></td>
                    <td style={{ color:"#374151", fontSize:12, maxWidth:300 }}>
                      {log.summary || <span style={{ color:"#d1d5db" }}>—</span>}
                    </td>
                    <td style={{ textAlign:"right", fontWeight:log.salesAed ? 600 : 400,
                      color: log.salesAed ? "#15803d" : "inherit" }}>
                      {log.salesAed ? fmt2(log.salesAed) : "—"}
                    </td>
                    <td style={{ textAlign:"right", fontWeight:log.collectionAed ? 600 : 400,
                      color: log.collectionAed ? "#1d4ed8" : "inherit" }}>
                      {log.collectionAed ? fmt2(log.collectionAed) : "—"}
                    </td>
                    <td style={{ fontSize:11, color:"#9ca3af", whiteSpace:"nowrap" }}>
                      {fmtDateTime(log.recordedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setModalOpen(false); }}>
          <VisitLogModal
            accounts={accounts}
            onSave={handleSave}
            onCancel={() => setModalOpen(false)}
          />
        </div>
      )}
    </>
  );
}
